import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, Package, AlertTriangle, Radio, Wifi, RotateCcw, ScanLine } from "lucide-react";
import PdaLayout from "./PdaLayout";
import { beep, primeAudio } from "@/lib/pdaSounds";
import { usePdaBarcode } from "@/hooks/usePdaBarcode";
import { usePdaScan } from "@/hooks/usePdaScan";
import usePdaScenarioHint from "@/hooks/usePdaScenarioHint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two-step delivery flow:
 *   1. BAG step  — scan every packed bag barcode at the hotel. Pleasant beep
 *                  on correct, error buzz on wrong, screen flashes green when
 *                  every packed bag is accounted for. Missed bags carry
 *                  through to /complete-delivery just like the web app.
 *   2. RFID step — operator presses the side trigger (or middle key) to
 *                  toggle RFID scanning. Live per-product counters update on
 *                  every batch. Strays / unknowns surface as separate tiles.
 *                  Completion beep fires when every ordered piece is scanned.
 */
export default function PdaDispatchDetail({ onLogout }) {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [step, setStep] = useState("bag"); // "bag" | "rfid"
  const [flash, setFlash] = useState(null); // "green" | "red" | null
  const [submitting, setSubmitting] = useState(false);
  // Full-screen alarm shown when the office reverted this order mid-transit
  // and the driver just scanned one of its bags. The driver must call in
  // before continuing.
  const [recallAlarm, setRecallAlarm] = useState(null);

  // Strays accumulated during RFID step (wrong_product or unknown_rfid).
  const [strays, setStrays] = useState([]);

  // Per-line manual ticks for operator override (set when the RFID won't read
  // or stock differs from spec). Pure frontend state — visual aid only.
  const [manuallyTicked, setManuallyTicked] = useState(() => new Set());

  const lastReadRef = useRef(""); // dedup for barcode key-repeats

  usePdaScenarioHint("Dispatch");
  useEffect(() => { primeAudio(); }, []);

  // Visual flash helper — small inline (used for RFID hits) and
  // full-screen (used for the big bag-scan feedback).
  const doFlash = (color) => {
    setFlash(color);
    setTimeout(() => setFlash(null), 350);
  };
  const [bigFlash, setBigFlash] = useState(null); // "ok" | "wrong" | "dup" — full-screen overlay
  const bigFlashTimer = useRef(null);
  const triggerBigFlash = useCallback((status) => {
    if (bigFlashTimer.current) clearTimeout(bigFlashTimer.current);
    setBigFlash(status);
    bigFlashTimer.current = setTimeout(() => setBigFlash(null), status === "wrong" ? 900 : 380);
  }, []);
  useEffect(() => () => { if (bigFlashTimer.current) clearTimeout(bigFlashTimer.current); }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/dispatch/orders/${orderId}/delivery`);
      setOrder(res.data);
      // If every packed bag is already scanned, jump straight to RFID step.
      const packed = (res.data.bundles || []).filter((b) => b.status === "packed");
      const allScanned = packed.length > 0 && packed.every((b) => !!b.bag_scanned_at_delivery);
      setStep(allScanned ? "rfid" : "bag");
    } catch (e) {
      toast.error("Couldn't load order");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { reload(); }, [reload]);

  // ─────────────────────── BAG STEP ───────────────────────

  // Fast lookup of every packed bag on this order by barcode. Rebuilt on
  // every state change so the local scan validation always reflects
  // ticks/untick done by the operator or a background refetch.
  const bagByBarcode = useMemo(() => {
    const m = new Map();
    for (const b of ((order?.bundles) || [])) {
      if (b.status === "packed" && b.barcode) m.set(b.barcode.toUpperCase(), b);
    }
    return m;
  }, [order]);

  const onBagBarcode = useCallback((raw) => {
    const code = String(raw || "").trim().toUpperCase();
    if (!code) return;
    // ── Local validation FIRST for zero-latency feedback ─────────────
    // (This is the "preloaded" behaviour matching Truck Loading —
    // no more waiting for the server round-trip on each scan.)
    const bag = bagByBarcode.get(code);
    if (!bag) {
      beep.error();
      triggerBigFlash("wrong");
      doFlash("red");
      try { navigator.vibrate?.([120, 60, 120, 60, 120]); } catch { /* ignored */ }
      toast.error("Bag not part of this order");
      return;
    }
    if (bag.bag_scanned_at_delivery) {
      // Already ticked — count as OK (operator's re-scan intent).
      beep.ok();
      triggerBigFlash("dup");
      doFlash("green");
      return;
    }
    // Optimistic tick: mark instantly so the operator sees green now.
    const scannedAt = new Date().toISOString();
    setOrder((prev) => prev ? {
      ...prev,
      bundles: (prev.bundles || []).map((b) =>
        b.barcode?.toUpperCase() === code ? { ...b, bag_scanned_at_delivery: scannedAt } : b
      ),
    } : prev);
    beep.ok();
    triggerBigFlash("ok");
    doFlash("green");
    try { navigator.vibrate?.(40); } catch { /* ignored */ }
    // ── Fire-and-forget persist ─────────────────────────────────────
    // The server enforces order_recalled / already-scanned checks. If
    // the recall alarm fires, we ring loudly and open the overlay; a
    // plain 400 rolls the tick back so the operator can re-scan.
    (async () => {
      try {
        await axios.post(`${API}/dispatch/orders/${orderId}/delivery-bag-scan`, { barcode: bag.barcode });
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (detail?.message || "");
        if (e?.response?.status === 409 && detail?.code === "order_recalled") {
          beep.error(); beep.error();
          triggerBigFlash("wrong");
          doFlash("red");
          setRecallAlarm({
            barcode: detail.barcode,
            order_number: detail.order_number,
            hotel_name: detail.hotel_name,
            recalled_by_name: detail.recalled_by_name,
            recalled_at: detail.recalled_at,
            message: detail.message,
          });
          // Roll back the optimistic tick
          setOrder((prev) => prev ? {
            ...prev,
            bundles: (prev.bundles || []).map((b) =>
              b.barcode?.toUpperCase() === code ? { ...b, bag_scanned_at_delivery: null } : b
            ),
          } : prev);
          return;
        }
        if (e?.response?.status === 400 && /already.*scanned/i.test(msg)) {
          // Server disagrees with local state — just refetch to reconcile.
          reload();
          return;
        }
        // Any other failure: revert + surface the error, don't spam beep
        // (the operator already heard the OK beep — mostly a network blip).
        setOrder((prev) => prev ? {
          ...prev,
          bundles: (prev.bundles || []).map((b) =>
            b.barcode?.toUpperCase() === code ? { ...b, bag_scanned_at_delivery: null } : b
          ),
        } : prev);
        toast.error(msg || "Save failed — try again");
      }
    })();
  }, [orderId, bagByBarcode, triggerBigFlash, reload]);

  const onManualBagTick = useCallback(async (bundle) => {
    // When the printed bag label has fallen off, the operator can manually
    // mark this bundle as delivered. Hits the same endpoint as a real scan
    // (passing the known barcode), so audit + bag_scanned_at_delivery match.
    if (!bundle?.barcode || bundle.bag_scanned_at_delivery) return;
    try {
      const res = await axios.post(
        `${API}/dispatch/orders/${orderId}/delivery-bag-scan`,
        { barcode: bundle.barcode, manual: true }
      );
      beep.ok();
      doFlash("green");
      setOrder((prev) => prev ? {
        ...prev,
        bundles: (prev.bundles || []).map((b) =>
          b.barcode === bundle.barcode ? { ...b, bag_scanned_at_delivery: res.data?.bag_scanned_at_delivery || new Date().toISOString() } : b
        ),
      } : prev);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "order_recalled") {
        beep.error();
        beep.error();
        doFlash("red");
        setRecallAlarm({
          barcode: detail.barcode,
          order_number: detail.order_number,
          hotel_name: detail.hotel_name,
          recalled_by_name: detail.recalled_by_name,
          recalled_at: detail.recalled_at,
          message: detail.message,
        });
        return;
      }
      beep.error();
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Couldn't mark delivered"));
    }
  // eslint-disable-next-line
  }, [orderId]);

  const onUndoBag = useCallback(async (bundle) => {
    // Operator changed their mind / mis-scanned. Reset the bundle locally
    // and call the backend's undo endpoint so the audit trail stays clean.
    if (!bundle?.id || !bundle.bag_scanned_at_delivery) return;
    try {
      await axios.delete(`${API}/dispatch/orders/${orderId}/delivery-bag-scan/${bundle.id}`);
      beep.ok();
      setOrder((prev) => prev ? {
        ...prev,
        bundles: (prev.bundles || []).map((b) =>
          b.id === bundle.id ? { ...b, bag_scanned_at_delivery: null, bag_scanned_manual: false } : b
        ),
      } : prev);
    } catch (e) {
      beep.error();
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Couldn't undo"));
    }
  // eslint-disable-next-line
  }, [orderId]);

  const clearAllScans = useCallback(async () => {
    if (!orderId) return;
    // Confirm — this can wipe dozens of scans in one tap.
    if (!window.confirm("Clear ALL scanned items for this order? You'll need to re-scan them.")) return;
    try {
      const res = await axios.delete(`${API}/dispatch/orders/${orderId}/delivery-scans`);
      const cleared = res?.data?.cleared || 0;
      setOrder((prev) => (prev ? { ...prev, delivery_scans: [] } : prev));
      setStrays([]);
      setManuallyTicked(new Set());
      beep.ok();
      toast.success(cleared ? `Cleared ${cleared} scan(s)` : "Nothing to clear");
    } catch (e) {
      beep.error();
      toast.error(e?.response?.data?.detail || "Couldn't clear scans");
    }
  }, [orderId]);

  // Side key (622) is barcode; bag step listens to it.
  usePdaBarcode(onBagBarcode, { enabled: step === "bag" });

  // ─────────────────────── RFID STEP ───────────────────────

  // Local cache of this order's expected RFIDs (built the moment the order
  // loads, i.e. during the bag-barcode step) → scans of expected tags get an
  // INSTANT beep + UI tick; the server confirms in the background.
  const expectedRef = useRef(new Map());
  useEffect(() => {
    const m = new Map();
    for (const b of order?.bundles || []) {
      for (const it of b.items || []) {
        if (it.rfid) m.set(it.rfid, { product_id: it.product_id, product_name: it.product_name, bundle_id: b.id });
      }
    }
    expectedRef.current = m;
  }, [order]);

  const onEpcs = useCallback(async (epcs) => {
    if (!epcs || epcs.length === 0) return;
    // 1) Optimistic local pass — expected tags tick instantly.
    let didOptimistic = false;
    setOrder((prev) => {
      if (!prev) return prev;
      const seen = new Set((prev.delivery_scans || []).map((s) => s.rfid));
      const additions = [];
      for (const e of epcs) {
        const info = expectedRef.current.get(e);
        if (info && !seen.has(e)) {
          additions.push({ rfid: e, ...info, _optimistic: true });
          seen.add(e);
        }
      }
      if (additions.length === 0) return prev;
      didOptimistic = true;
      return { ...prev, delivery_scans: [...(prev.delivery_scans || []), ...additions] };
    });
    if (didOptimistic) beep.tick();

    // 2) Server pass — authoritative (persists scans, handles transfers,
    //    wrong-product and unknown tags).
    try {
      const res = await axios.post(
        `${API}/dispatch/orders/${orderId}/delivery-scan-bulk`,
        // Always allow implicit transfers. If an RFID is currently marked
        // as "at_hotel" for a DIFFERENT hotel, the backend will treat this
        // scan as a transfer (event_type='transfer') and count it into
        // delivery_scans normally. Without this flag, transfer-candidates
        // are stashed in transfers_required and silently disappear from
        // the UI — the driver sees "no reaction" on the scan.
        { rfids: epcs, force_transfer: true }
      );
      const { scans = [], unknown = [], wrong_product = [], already_at_hotel = [], summary = {} } = res.data || {};
      if (scans.length > 0) {
        // Only chirp when the optimistic pass didn't already (e.g. transfers
        // from another hotel that aren't in this order's expected set).
        setOrder((prev) => {
          if (!prev) return prev;
          const serverByRfid = new Map(scans.map((s) => [s.rfid, s]));
          // Replace optimistic placeholders with the authoritative rows.
          const kept = (prev.delivery_scans || []).filter((s) => !(s._optimistic && serverByRfid.has(s.rfid)));
          const seen = new Set(kept.map((s) => s.rfid));
          const fresh = scans.filter((s) => !seen.has(s.rfid));
          if (fresh.length > 0 && !didOptimistic) beep.tick();
          return { ...prev, delivery_scans: [...kept, ...fresh] };
        });
      }
      // Roll back optimistic placeholders for any RFID the server told us
      // to skip (unknown / already-at-this-hotel / wrong-product). They
      // must NOT count as delivered this trip.
      const rolledBack = new Set([
        ...unknown,
        ...already_at_hotel,
        ...wrong_product.map((w) => w.rfid),
      ]);
      if (didOptimistic && rolledBack.size > 0) {
        setOrder((prev) => prev ? {
          ...prev,
          delivery_scans: (prev.delivery_scans || []).filter((s) => !(s._optimistic && rolledBack.has(s.rfid))),
        } : prev);
      }
      if (unknown.length || wrong_product.length || already_at_hotel.length) {
        setStrays((prev) => {
          const seen = new Set(prev.map((s) => s.rfid));
          const next = [...prev];
          unknown.forEach((rfid) => {
            if (!seen.has(rfid)) { next.push({ rfid, product_name: "Unknown", code: "unknown_rfid" }); seen.add(rfid); }
          });
          already_at_hotel.forEach((rfid) => {
            if (!seen.has(rfid)) { next.push({ rfid, product_name: "Already at this hotel", code: "already_at_hotel" }); seen.add(rfid); }
          });
          wrong_product.forEach((w) => {
            if (!seen.has(w.rfid)) { next.push({ rfid: w.rfid, product_name: w.product_name || "?", code: "wrong_product" }); seen.add(w.rfid); }
          });
          return next;
        });
      }
      if (summary.unknown || summary.wrong_product) {
        // Soft error chirp — operator is alerted but scanning continues.
        // `already_at_hotel` is silent (it's an expected re-scan, not an error).
        beep.error();
      }
    } catch (e) {
      // Roll back optimistic entries for this burst so counts don't lie.
      if (didOptimistic) {
        const burst = new Set(epcs);
        setOrder((prev) => prev ? {
          ...prev,
          delivery_scans: (prev.delivery_scans || []).filter((s) => !(s._optimistic && burst.has(s.rfid))),
        } : prev);
      }
      toast.error("Bulk scan failed");
    }
  }, [orderId]);

  usePdaScan(onEpcs, { enabled: step === "rfid" });

  // ─────────────────────── DERIVED STATS ───────────────────────

  const stats = useMemo(() => {
    if (!order) return null;
    // Adjustment pseudo-items have no RFID tags — they're tracked at bag
    // level only, so exclude them from RFID scan expectations.
    const items = (order.items || []).filter((it) => !it.is_adjustment);
    const scans = order.delivery_scans || [];
    const expectedByPid = {};
    for (const it of items) {
      if (it.product_id) expectedByPid[it.product_id] = (expectedByPid[it.product_id] || 0) + (it.quantity || 0);
    }
    const newByPid = {};
    let totalNew = 0, totalRescan = 0;
    // Defensive dedup by rfid in case of any double-push.
    const seenRfids = new Set();
    // Only items whose product is in the order influence "Delivered" counts.
    // Backend already filters wrong_product out of scans, but we double-check
    // here so a stale frontend state can't inflate the totals.
    const orderPidSet = new Set(items.map((it) => it.product_id));
    for (const s of scans) {
      if (s.rfid && seenRfids.has(s.rfid)) continue;
      if (s.rfid) seenRfids.add(s.rfid);
      // Backend uses `event_type`; fall back to `kind` for older shapes.
      const ev = s.event_type || s.kind;
      if (ev === "rescan") { totalRescan++; continue; }
      if (s.product_id && orderPidSet.has(s.product_id)) {
        newByPid[s.product_id] = (newByPid[s.product_id] || 0) + 1;
        totalNew++;
      }
    }
    const lines = items.map((it) => ({
      product_id: it.product_id,
      product_name: it.product_name,
      expected: it.quantity || 0,
      delivered: newByPid[it.product_id] || 0,
    }));
    const totalExpected = lines.reduce((s, l) => s + l.expected, 0);
    const allComplete = totalExpected > 0 && lines.every((l) => l.delivered >= l.expected);
    return { lines, totalExpected, totalNew, totalRescan, allComplete };
  }, [order]);

  // Completion beep fires the moment every ordered piece is accounted for.
  const completeBeepedRef = useRef(false);
  useEffect(() => {
    if (step === "rfid" && stats?.allComplete && !completeBeepedRef.current) {
      completeBeepedRef.current = true;
      beep.complete();
    }
  }, [step, stats?.allComplete]);

  // Bag-step completion: pleasant double-flash + auto-advance.
  const bagCompleteBeepedRef = useRef(false);
  const bagStats = useMemo(() => {
    if (!order) return { packed: 0, scanned: 0 };
    const packed = (order.bundles || []).filter((b) => b.status === "packed");
    return { packed: packed.length, scanned: packed.filter((b) => b.bag_scanned_at_delivery).length };
  }, [order]);
  useEffect(() => {
    if (step === "bag" && bagStats.packed > 0 && bagStats.scanned === bagStats.packed && !bagCompleteBeepedRef.current) {
      bagCompleteBeepedRef.current = true;
      beep.complete();
      doFlash("green");
    }
  }, [step, bagStats]);

  // ─────────────────────── CONFIRM ───────────────────────

  const onConfirm = async () => {
    if (!order || submitting) return;
    const packed = (order.bundles || []).filter((b) => b.status === "packed");
    const scanned = packed.filter((b) => b.bag_scanned_at_delivery);
    if (scanned.length === 0) {
      toast.error("Scan at least one bag first");
      return;
    }
    const missed = packed.filter((b) => !b.bag_scanned_at_delivery);
    const extras = strays.filter((s) => s.code === "wrong_product");

    // Extras (items scanned that aren't on this order) are registered to the
    // hotel automatically — no popup, per operator request.
    const registerExtras = extras.length > 0;
    let confirmMissed = true;
    if (missed.length > 0) {
      confirmMissed = window.confirm(
        `${missed.length} bag(s) weren't scanned. They'll be split into a NEW delivery for the missing items.\n\nContinue?`
      );
      if (!confirmMissed) return;
    }
    setSubmitting(true);
    try {
      if (registerExtras && extras.length > 0) {
        await axios.post(
          `${API}/dispatch/orders/${orderId}/register-strays-to-hotel`,
          { rfids: extras.map((e) => e.rfid) }
        );
      }
      await axios.post(`${API}/dispatch/orders/${orderId}/complete-delivery`, {});
      beep.complete();
      toast.success("Delivery complete");
      navigate("/pda/dispatch", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Couldn't complete delivery"));
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────── RENDER ───────────────────────

  if (loading) {
    return (
      <PdaLayout title="Dispatch" onBack={() => navigate("/pda/dispatch")} onLogout={onLogout}>
        <div className="py-10 flex flex-col items-center text-neutral-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PdaLayout>
    );
  }
  if (!order) return null;

  const flashClass =
    flash === "green" ? "bg-emerald-200" :
    flash === "red"   ? "bg-rose-200"    : "";

  const packed = (order.bundles || []).filter((b) => b.status === "packed");

  // Recall alarm — full-screen blocking overlay.
  if (recallAlarm) {
    return (
      <div
        data-testid="pda-recall-alarm"
        className="fixed inset-0 z-[9999] bg-red-700 text-white flex flex-col items-center justify-center px-6 py-8 animate-pulse"
      >
        <div className="text-[64px] font-black leading-none mb-2">STOP</div>
        <div className="text-2xl font-black tracking-wider mb-6">ORDER RECALLED</div>
        <div className="rounded-xl bg-red-900/60 border-2 border-white/60 px-5 py-4 max-w-md w-full space-y-2 text-center">
          <p className="text-lg font-bold">
            {recallAlarm.order_number}
            {recallAlarm.hotel_name ? ` · ${recallAlarm.hotel_name}` : ""}
          </p>
          <p className="text-base">
            Bag <span className="font-mono">{recallAlarm.barcode}</span> was recalled by the office
            {recallAlarm.recalled_by_name ? ` (${recallAlarm.recalled_by_name})` : ""}
            {recallAlarm.recalled_at ? ` at ${new Date(recallAlarm.recalled_at).toLocaleString()}` : ""}.
          </p>
          <p className="text-lg font-bold mt-3">
            DO NOT deliver. Contact the office immediately.
          </p>
        </div>
        <button
          data-testid="pda-recall-alarm-ack"
          onClick={() => setRecallAlarm(null)}
          className="mt-8 h-14 w-full max-w-md rounded-lg bg-white text-red-800 font-black text-lg"
        >
          I&apos;ve contacted the office — dismiss
        </button>
      </div>
    );
  }

  return (
    <PdaLayout
      title={order.hotel_name || "Dispatch"}
      subtitle={`#${order.order_number}`}
      onBack={() => navigate("/pda/dispatch")}
      onLogout={onLogout}
      footer={
        step === "rfid" ? (
          <button
            onClick={onConfirm}
            disabled={submitting}
            data-testid="pda-dispatch-confirm"
            className="w-full h-12 rounded-md bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Confirm delivery"}
          </button>
        ) : null
      }
    >
      {/* Step tab */}
      <div className="flex rounded-md border bg-white mb-3">
        <button
          onClick={() => setStep("bag")}
          data-testid="pda-step-bag"
          className={`flex-1 py-2 text-sm font-bold ${step === "bag" ? "bg-amber-400 text-neutral-900" : "text-neutral-600"} rounded-l-md`}
        >
          1 · Bags  {bagStats.scanned}/{bagStats.packed}
        </button>
        <button
          onClick={() => setStep("rfid")}
          data-testid="pda-step-rfid"
          className={`flex-1 py-2 text-sm font-bold ${step === "rfid" ? "bg-amber-400 text-neutral-900" : "text-neutral-600"} rounded-r-md`}
        >
          2 · Items {stats?.totalNew || 0}/{stats?.totalExpected || 0}
        </button>
      </div>

      <div className={`transition-colors duration-300 rounded-md ${flashClass} -mx-1 px-1`}>
        {step === "bag" ? (
          <BagStep order={order} packed={packed} onManualBagTick={onManualBagTick} onUndoBag={onUndoBag} onBagBarcode={onBagBarcode} />
        ) : (
          <>
          {/* Mini-stocktake preload: what this hotel already holds. Scanning
              only registers NEW arrivals; existing stock stays untouched. */}
          {(order?.hotel_inventory || []).length > 0 && (
            <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 p-2" data-testid="pda-hotel-inventory">
              <div className="text-[11px] font-bold uppercase tracking-wide text-sky-800 mb-1">
                Already at this hotel · {(order.hotel_inventory || []).reduce((s, x) => s + (x.count || 0), 0)} pcs
              </div>
              <div className="flex flex-wrap gap-1.5">
                {order.hotel_inventory.map((h) => (
                  <span key={h.product_id} className="text-xs bg-white border border-sky-200 rounded px-1.5 py-0.5 text-sky-900">
                    {h.product_name} × {h.count}
                  </span>
                ))}
              </div>
              <div className="text-[10px] text-sky-700 mt-1">
                Scans matching existing stock are skipped — only new arrivals are registered. Unscanned items stay unchanged.
              </div>
            </div>
          )}
          <RfidStep
            stats={stats}
            strays={strays}
            onClearStrays={() => setStrays([])}
            onClearAllScans={clearAllScans}
            manuallyTicked={manuallyTicked}
            onToggleManualTick={(pid) => {
              setManuallyTicked((prev) => {
                const next = new Set(prev);
                if (next.has(pid)) next.delete(pid); else next.add(pid);
                return next;
              });
              beep.ok();
            }}
          />
          </>
        )}

        {/* Full-screen colour flash — matches Truck Loading. Auto-dismisses.
            Rendered outside the scrollable layout so it always covers the
            entire viewport during a scan. */}
        {bigFlash && (
          <div
            data-testid={`pda-dispatch-flash-${bigFlash}`}
            className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center pointer-events-none ${
              bigFlash === "wrong"
                ? "bg-rose-600 animate-pulse"
                : bigFlash === "dup"
                  ? "bg-amber-400/95"
                  : "bg-emerald-500"
            }`}
          >
            {bigFlash === "wrong" ? (
              <>
                <XCircle className="h-32 w-32 text-white mb-4" strokeWidth={2.5} />
                <div className="font-display font-black text-white text-5xl tracking-wide">STOP</div>
                <div className="font-display font-bold text-white/90 text-2xl mt-2">Wrong bag</div>
              </>
            ) : bigFlash === "dup" ? (
              <>
                <CheckCircle className="h-32 w-32 text-amber-900 mb-4" strokeWidth={2.5} />
                <div className="font-display font-black text-amber-900 text-4xl">Already scanned</div>
              </>
            ) : (
              <>
                <CheckCircle className="h-32 w-32 text-white mb-4" strokeWidth={2.5} />
                <div className="font-display font-black text-white text-5xl">OK</div>
              </>
            )}
          </div>
        )}
      </div>
    </PdaLayout>
  );
}

export function BagStep({ order, packed, onManualBagTick, onUndoBag, onBagBarcode }) {
  // Auto-focused capture input for keyboard-mode PDA scanners.
  //
  // Why this is complex: the OEM scanner on the user's PDA delivers the
  // barcode via Android IME (soft-keyboard IME composition), so:
  //   • text lands in the IME's pre-edit buffer, NOT the input's .value
  //   • React's onChange doesn't fire until composition commits
  //   • the scanner does NOT send Enter to finalise
  //
  // Fix: pair three complementary paths so at least one lands the value:
  //   1. onKeyDown Enter → submit (for scanners that DO send Enter)
  //   2. compositionend → read current value + submit (IME commit)
  //   3. Polling loop (every 100 ms) → if the DOM value has been stable
  //      for >=350 ms and has length >=3, submit. This catches IME
  //      scanners that never fire compositionend/onChange properly.
  const inputRef = useRef(null);
  const submitInFlightRef = useRef(false);

  const doSubmit = useCallback((raw) => {
    if (submitInFlightRef.current) return;
    const code = String(raw || "").trim().replace(/[\r\n\t]+/g, "");
    if (!code || code.length < 3) return;
    submitInFlightRef.current = true;
    // Clear the DOM value directly — we're bypassing React state for
    // reliability under IME composition.
    if (inputRef.current) inputRef.current.value = "";
    try {
      onBagBarcode?.(code);
    } finally {
      // Release the guard on next tick + refocus.
      setTimeout(() => {
        submitInFlightRef.current = false;
        inputRef.current?.focus();
      }, 60);
    }
  }, [onBagBarcode]);

  useEffect(() => {
    // Focus on mount.
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    // Polling fallback — the most reliable path for IME scanners.
    // Poll every 50 ms and submit if the DOM value has been stable and
    // non-empty for >=150 ms. Total worst-case latency ≈ 200 ms — fast
    // enough to feel instant while still tolerating IME char bursts
    // (real scanners fire chars <10 ms apart, so 150 ms of quiet is a
    // solid end-of-scan signal).
    let lastVal = "";
    let stableSince = Date.now();
    const id = setInterval(() => {
      const el = inputRef.current;
      if (!el || submitInFlightRef.current) return;
      const cur = el.value;
      if (cur !== lastVal) {
        lastVal = cur;
        stableSince = Date.now();
        return;
      }
      if (cur.length >= 3 && Date.now() - stableSince >= 150) {
        doSubmit(cur);
        lastVal = "";
        stableSince = Date.now();
      }
    }, 50);
    return () => clearInterval(id);
  }, [doSubmit]);

  useEffect(() => {
    // Native compositionend + compositionupdate — IME commit events. Read
    // the .value (compositionend) or use e.data (compositionupdate) to
    // grab pre-edit text for the debounce loop.
    const el = inputRef.current;
    if (!el) return undefined;
    const onCompEnd = () => doSubmit(el.value);
    const onCompUpdate = (e) => {
      // Write pre-edit text into the input's own value so the polling
      // loop can pick it up even if the IME never commits.
      if (e.data) el.value = e.data;
    };
    el.addEventListener("compositionend", onCompEnd);
    el.addEventListener("compositionupdate", onCompUpdate);
    return () => {
      el.removeEventListener("compositionend", onCompEnd);
      el.removeEventListener("compositionupdate", onCompUpdate);
    };
  }, [doSubmit]);

  const refocus = () => {
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <div className="space-y-2" onClick={refocus}>
      <div className="rounded-md border bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
        <Wifi className="h-4 w-4" />
        Press the <span className="font-bold">side trigger (barcode)</span> to scan each bag. Tap <span className="font-bold">Manual ✓</span> if the label is missing — tap the green tick again to undo.
      </div>

      {/* VISIBLE barcode capture input — placed at the top so the driver
          can see the scanned code before it auto-submits. Uncontrolled
          (we read/write .value directly) so IME composition doesn't get
          lost between React renders. */}
      <div className="rounded-md border-2 border-brand-navy bg-white px-3 py-2 flex items-center gap-2">
        <ScanLine className="h-5 w-5 text-brand-navy shrink-0" />
        <input
          ref={inputRef}
          type="text"
          defaultValue=""
          placeholder="Scan bag barcode here…"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              doSubmit(e.currentTarget.value);
            }
          }}
          onBlur={refocus}
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          className="flex-1 bg-transparent outline-none text-base font-mono placeholder:text-neutral-400"
          data-testid="pda-bag-capture-input"
          aria-label="Barcode scan capture"
        />
      </div>

      <div className="space-y-1.5">
        {packed.map((b) => {
          const ok = !!b.bag_scanned_at_delivery;
          const items = b.items || [];
          const totalPcs = items.reduce((s, it) => s + (it.quantity || 0), 0);
          return (
            <div
              key={b.id || b.barcode}
              data-testid={`pda-bag-${b.barcode}`}
              className={`rounded-md border-2 px-3 py-2 ${ok ? "bg-emerald-50 border-emerald-400" : "bg-amber-50 border-amber-300"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {ok ? (
                    <button
                      onClick={() => onUndoBag?.(b)}
                      data-testid={`pda-bag-undo-${b.barcode}`}
                      title="Undo — mark as not delivered"
                      className="text-emerald-600 hover:text-rose-600 transition-colors shrink-0"
                    >
                      <CheckCircle className="h-6 w-6" />
                    </button>
                  ) : (
                    <XCircle className="h-6 w-6 text-amber-500 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="font-display font-bold text-base">Bag #{b.bundle_no}</div>
                    <div className="text-[10px] font-mono text-neutral-500 truncate">{b.barcode}</div>
                  </div>
                </div>
                {!ok && onManualBagTick ? (
                  <button
                    onClick={() => onManualBagTick(b)}
                    data-testid={`pda-bag-manual-${b.barcode}`}
                    className="text-xs font-bold rounded-md border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-1.5 whitespace-nowrap"
                  >
                    Manual ✓
                  </button>
                ) : ok ? (
                  <button
                    onClick={() => onUndoBag?.(b)}
                    data-testid={`pda-bag-undo-btn-${b.barcode}`}
                    className="text-xs font-bold rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 px-3 py-1.5 whitespace-nowrap"
                  >
                    Undo
                  </button>
                ) : null}
              </div>
              {items.length > 0 && (
                <div className="mt-2 pl-9 text-[11px] text-neutral-600">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-400 mb-0.5">Contents · {totalPcs} pcs</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                    {items.map((it, idx) => (
                      <span key={idx} className="font-mono">{it.product_name}<span className="text-neutral-400"> ·</span> {it.quantity}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {packed.length === 0 ? (
        <div className="text-sm text-neutral-500 text-center py-6">No packed bags on this order</div>
      ) : null}
    </div>
  );
}

// ─────────────────────── RFID STEP UI ───────────────────────

function RfidStep({ stats, strays, onClearStrays, onClearAllScans, manuallyTicked, onToggleManualTick }) {
  if (!stats) return null;
  const extras = strays.filter((s) => s.code === "wrong_product");
  const unknowns = strays.filter((s) => s.code === "unknown_rfid");
  const anyScanned = (stats.totalNew || 0) + (stats.totalRescan || 0) > 0;
  // Treat manually-ticked lines as visually complete even if delivered < expected.
  const isDone = (l) => l.delivered >= l.expected || manuallyTicked?.has(l.product_id);
  // Over-scan detection — scanning MORE of the same product than the order
  // asked for. Previously this was silent (just showed 12/10 as normal); now
  // we render an amber warning strip similar to how wrong_product extras are
  // surfaced, so the driver can hand the extras back before confirming.
  const overLines = (stats.lines || []).filter((l) => l.delivered > l.expected);
  const totalOverscan = overLines.reduce((s, l) => s + (l.delivered - l.expected), 0);
  return (
    <div className="space-y-3">
      <div className="rounded-md border bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
        <Radio className="h-4 w-4" />
        Press the <span className="font-bold">middle key</span> to start / stop RFID scanning. Tap a row to manually ✓ override.
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-2">
        <Stat label="Delivered" value={stats.totalNew} of={stats.totalExpected} tone="emerald" testid="pda-stat-new" />
        <Stat label="Re-scan" value={stats.totalRescan} tone="sky" testid="pda-stat-rescan" />
        <Stat label="Strays" value={strays.length} tone="amber" testid="pda-stat-strays" />
      </div>

      {/* Clear-all shortcut — driver can wipe every scan and start over
          (used e.g. when they grabbed the wrong bag). Only appears once
          something has been scanned so it doesn't clutter the empty state. */}
      {anyScanned && onClearAllScans && (
        <button
          type="button"
          onClick={onClearAllScans}
          data-testid="pda-clear-all-scans"
          className="w-full rounded-md border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 text-xs font-semibold py-2 flex items-center justify-center gap-1.5"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Clear all scans &amp; start over
        </button>
      )}

      {/* Per product */}
      <div className="rounded-md border bg-white divide-y">
        {stats.lines.map((l) => {
          const done = isDone(l);
          const manual = manuallyTicked?.has(l.product_id);
          const overBy = Math.max(0, l.delivered - l.expected);
          const isOver = overBy > 0;
          return (
            <button
              key={l.product_id}
              data-testid={`pda-line-${l.product_id}`}
              onClick={() => onToggleManualTick?.(l.product_id)}
              className={`w-full px-3 py-2 flex items-center justify-between hover:bg-amber-50 ${
                isOver
                  ? "bg-amber-100/70 border-l-4 border-amber-500"
                  : done
                  ? "bg-emerald-50/30"
                  : ""
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {isOver ? (
                  <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                ) : done ? (
                  <CheckCircle className={`h-5 w-5 shrink-0 ${manual ? "text-amber-600" : "text-emerald-600"}`} />
                ) : (
                  <span className="h-5 w-5 rounded-full border border-neutral-300 shrink-0" />
                )}
                <span className="font-medium truncate text-sm text-left">{l.product_name}</span>
                {isOver && (
                  <span
                    data-testid={`pda-line-over-${l.product_id}`}
                    className="text-[9px] font-bold uppercase tracking-wider text-amber-900 bg-amber-200 rounded px-1.5 py-0.5 ml-1"
                  >
                    +{overBy} over
                  </span>
                )}
                {!isOver && manual ? <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 ml-1">manual</span> : null}
              </div>
              <span
                className={`font-mono font-bold tabular-nums text-sm ${
                  isOver ? "text-amber-800" : done ? (manual ? "text-amber-700" : "text-emerald-700") : ""
                }`}
              >
                {l.delivered} / {l.expected}
              </span>
            </button>
          );
        })}
      </div>

      {/* Overscan banner — visible whenever any line has delivered > expected. */}
      {overLines.length > 0 && (
        <div className="rounded-md border-2 border-amber-500 bg-amber-50" data-testid="pda-overscan-panel">
          <div className="px-3 py-2 border-b border-amber-300 text-xs uppercase tracking-wider text-amber-900 flex items-center gap-1.5 font-bold">
            <AlertTriangle className="h-4 w-4" /> Over-scanned · +{totalOverscan} extra
          </div>
          <div className="px-3 py-2 text-[11px] text-amber-900">
            You&apos;ve scanned more items than this order needs. Double-check you haven&apos;t loaded another hotel&apos;s linen by mistake.
          </div>
          <div className="max-h-40 overflow-y-auto divide-y divide-amber-200">
            {overLines.map((l) => (
              <div key={l.product_id} className="px-3 py-1.5 text-sm flex items-center justify-between">
                <span className="font-medium truncate">{l.product_name}</span>
                <span className="font-mono text-xs text-amber-800 ml-2 shrink-0">
                  {l.delivered} / {l.expected} · <span className="font-bold">+{l.delivered - l.expected}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {extras.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50" data-testid="pda-extras-panel">
          <div className="px-3 py-2 border-b border-amber-200 text-xs uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Possible extras · {extras.length}
          </div>
          <div className="px-3 py-2 text-[11px] text-amber-900/80">These items don't belong to this order — they'll be registered to this hotel automatically when you confirm the delivery.</div>
          <div className="max-h-40 overflow-y-auto divide-y divide-amber-200">
            {extras.map((s) => (
              <div key={s.rfid} className="px-3 py-1.5 text-sm flex items-center justify-between">
                <span className="font-medium">{s.product_name}</span>
                <span className="font-mono text-[10px] text-amber-800/60 truncate ml-2">{s.rfid.slice(-10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {unknowns.length > 0 && (
        <div className="rounded-md border bg-neutral-50" data-testid="pda-unknown-panel">
          <div className="px-3 py-2 border-b text-xs uppercase tracking-wider text-neutral-700 flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" /> Unknown tags · {unknowns.length}
          </div>
          <div className="px-3 py-2 text-[11px] text-neutral-600">Not registered in the system — likely the hotel's own RFIDs. Ignored.</div>
        </div>
      )}

      {strays.length > 0 && (
        <button onClick={onClearStrays} data-testid="pda-strays-clear" className="text-xs text-rose-700 underline">
          Clear strays panel
        </button>
      )}
    </div>
  );
}

function Stat({ label, value, of, tone, testid }) {
  const cls = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    sky:     "bg-sky-50 text-sky-800 border-sky-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
  }[tone] || "bg-neutral-50 text-neutral-800";
  return (
    <div className={`rounded-md border px-2 py-2 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-75">{label}</div>
      <div className="font-display font-bold text-2xl">{value}{of !== undefined ? <span className="text-base opacity-70"> / {of}</span> : null}</div>
    </div>
  );
}
